import type {
  StorageLocationsResponse,
  TemperatureHistory,
  TemperatureReading,
} from '../types/storage';

function generateReadings(
  baseTemp: number,
  variance: number,
  hours: number,
  unit: 'C' | 'F' = 'C'
): TemperatureReading[] {
  const now = new Date();
  const totalReadings = hours * 4;

  // Simple seeded pseudo-random for deterministic mock data
  // nosemgrep: pseudo-random
  const seed = baseTemp * 1000 + variance * 100 + hours;
  const seededRandom = (index: number) => {
    const x = Math.sin(seed + index) * 10000;
    return x - Math.floor(x);
  };

  return Array.from({ length: totalReadings + 1 }, (_, idx) => {
    const i = totalReadings - idx;
    const timestamp = new Date(now.getTime() - i * 15 * 60 * 1000);
    const randomVariance = (seededRandom(idx) - 0.5) * variance * 2;
    return {
      timestamp: timestamp.toISOString(),
      value: Math.round((baseTemp + randomVariance) * 10) / 10,
      unit,
    };
  });
}

export const MOCK_STORAGE_LOCATIONS: StorageLocationsResponse = {
  data: [
    {
      locationId: 'loc-freezer-01',
      name: 'Main Freezer',
      type: 'freezer',
      isActive: true,
      sensorId: 'sensor-001',
      currentTemperature: {
        timestamp: new Date().toISOString(),
        value: -18.5,
        unit: 'C',
      },
      temperatureStatus: 'normal',
      thresholds: {
        minCritical: -25,
        minWarning: -22,
        maxWarning: -15,
        maxCritical: -10,
        unit: 'C',
      },
      alertConfig: {
        enabled: true,
        emailNotifications: true,
        smsNotifications: true,
        emailRecipients: ['chef@example.com', 'manager@example.com'],
        smsRecipients: ['+1234567890'],
        escalationDelayMinutes: 15,
      },
      itemCount: 145,
      lastUpdated: new Date().toISOString(),
    },
    {
      locationId: 'loc-freezer-02',
      name: 'Seafood Freezer',
      type: 'freezer',
      isActive: true,
      sensorId: 'sensor-002',
      currentTemperature: {
        timestamp: new Date().toISOString(),
        value: -12.8,
        unit: 'C',
      },
      temperatureStatus: 'warning',
      thresholds: {
        minCritical: -25,
        minWarning: -22,
        maxWarning: -15,
        maxCritical: -10,
        unit: 'C',
      },
      alertConfig: {
        enabled: true,
        emailNotifications: true,
        smsNotifications: false,
        emailRecipients: ['chef@example.com'],
        smsRecipients: [],
        escalationDelayMinutes: 10,
      },
      itemCount: 67,
      lastUpdated: new Date().toISOString(),
    },
    {
      locationId: 'loc-cooler-01',
      name: 'Dairy Cooler',
      type: 'cooler',
      isActive: true,
      sensorId: 'sensor-003',
      currentTemperature: {
        timestamp: new Date().toISOString(),
        value: 4.2,
        unit: 'C',
      },
      temperatureStatus: 'normal',
      thresholds: {
        minCritical: 0,
        minWarning: 2,
        maxWarning: 6,
        maxCritical: 8,
        unit: 'C',
      },
      alertConfig: {
        enabled: true,
        emailNotifications: true,
        smsNotifications: true,
        emailRecipients: ['chef@example.com'],
        smsRecipients: ['+1234567890'],
        escalationDelayMinutes: 15,
      },
      itemCount: 89,
      lastUpdated: new Date().toISOString(),
    },
    {
      locationId: 'loc-cooler-02',
      name: 'Produce Cooler',
      type: 'cooler',
      isActive: true,
      sensorId: 'sensor-004',
      currentTemperature: {
        timestamp: new Date().toISOString(),
        value: 9.1,
        unit: 'C',
      },
      temperatureStatus: 'critical',
      thresholds: {
        minCritical: 0,
        minWarning: 2,
        maxWarning: 6,
        maxCritical: 8,
        unit: 'C',
      },
      alertConfig: {
        enabled: true,
        emailNotifications: true,
        smsNotifications: true,
        emailRecipients: ['chef@example.com', 'manager@example.com'],
        smsRecipients: ['+1234567890', '+0987654321'],
        escalationDelayMinutes: 5,
      },
      itemCount: 112,
      lastUpdated: new Date().toISOString(),
    },
    {
      locationId: 'loc-dry-01',
      name: 'Dry Storage A',
      type: 'dry-storage',
      isActive: true,
      sensorId: 'sensor-005',
      currentTemperature: {
        timestamp: new Date().toISOString(),
        value: 21.3,
        unit: 'C',
      },
      temperatureStatus: 'normal',
      thresholds: {
        minCritical: 10,
        minWarning: 15,
        maxWarning: 25,
        maxCritical: 30,
        unit: 'C',
      },
      alertConfig: {
        enabled: false,
        emailNotifications: false,
        smsNotifications: false,
        emailRecipients: [],
        smsRecipients: [],
        escalationDelayMinutes: 30,
      },
      itemCount: 234,
      lastUpdated: new Date().toISOString(),
    },
    {
      locationId: 'loc-bar-01',
      name: 'Bar Cooler',
      type: 'bar-storage',
      isActive: true,
      sensorId: 'sensor-006',
      currentTemperature: {
        timestamp: new Date().toISOString(),
        value: 3.8,
        unit: 'C',
      },
      temperatureStatus: 'normal',
      thresholds: {
        minCritical: 0,
        minWarning: 2,
        maxWarning: 6,
        maxCritical: 8,
        unit: 'C',
      },
      alertConfig: {
        enabled: true,
        emailNotifications: true,
        smsNotifications: false,
        emailRecipients: ['bar@example.com'],
        smsRecipients: [],
        escalationDelayMinutes: 20,
      },
      itemCount: 56,
      lastUpdated: new Date().toISOString(),
    },
  ],
  meta: { siteId: 'site-celebration' },
};

export function getMockTemperatureHistory(locationId: string): TemperatureHistory {
  const location = MOCK_STORAGE_LOCATIONS.data.find((l) => l.locationId === locationId);
  if (!location) {
    return {
      locationId,
      readings: [],
      alerts: [],
    };
  }

  const baseTemp = location.currentTemperature?.value ?? 0;
  const variance = location.type === 'freezer' ? 3 : 2;

  const readings = generateReadings(baseTemp, variance, 24, location.thresholds.unit);

  if (location.temperatureStatus === 'normal') {
    return {
      locationId,
      readings,
      alerts: [],
    };
  }

  const statusLabel = location.temperatureStatus === 'critical' ? 'critically ' : '';
  const currentTemp = location.currentTemperature?.value ?? 0;

  const alerts = [
    {
      id: `alert-${locationId}-1`,
      timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
      type: location.temperatureStatus as 'warning' | 'critical',
      temperature: currentTemp,
      message: `Temperature ${statusLabel}out of range: ${currentTemp}°C`,
      acknowledged: false,
      acknowledgedBy: null,
      acknowledgedAt: null,
    },
    {
      id: `alert-${locationId}-2`,
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      type: 'warning' as const,
      temperature: location.thresholds.maxWarning + 0.5,
      message: `Temperature approaching limit: ${location.thresholds.maxWarning + 0.5}°C`,
      acknowledged: true,
      acknowledgedBy: 'John Smith',
      acknowledgedAt: new Date(Date.now() - 1.5 * 60 * 60 * 1000).toISOString(),
    },
  ];

  return {
    locationId,
    readings,
    alerts,
  };
}
